// disable member ordering to not mix event properties
// as they're already well-structured
/* eslint-disable @typescript-eslint/member-ordering */
import dayjs from 'dayjs';

import { DEFAULT_SERVICE_NAME } from '~/common/constants/search';
import {
  OVERNIGHT_SERVICE_NAMES,
  RWB_SERVICE_NAMES,
} from '~/components/SearchFiltersForm/constants';
import { BOOKING_TYPE_RWB } from '~/components/utils/search';

import { SERVICE_TYPE_MAPPING } from './constants';

import type { Chronology, SearchFilters, SearchResults } from '~/common/types/search';

type BaseProperties = {
  suburb: string;
  service_type: string;
  repeat_frequency: string;
};

type DateProperties = {
  check_in: string;
  check_out?: string;
  overnight_length_of_stay?: number;
  rwb_service_number_days?: number;
  single_service_number_days?: number;
  lead_time: number;
  daytime_service_monday?: boolean;
  daytime_service_tuesday?: boolean;
  daytime_service_wednesday?: boolean;
  daytime_service_thursday?: boolean;
  daytime_service_friday?: boolean;
  daytime_service_saturday?: boolean;
  daytime_service_sunday?: boolean;
};

type PetTypesProperties = {
  small_dog_number: number;
  medium_dog_number: number;
  large_dog_number: number;
  giant_dog_number: number;
  puppy_number: number;
  cat_number: number;
  critter_number: number;
  bird_number: number;
};

type FiltersDialogEventProperties = {
  about_sitter?: string[];
  lower_price?: string;
  pets_kids_sitter_home?: string[];
  sitter_home?: string[];
  sort?: string;
  upper_price?: string;
  reviews_min?: string;
};

type SearchAlgorithmProperties = {
  search_algorithmVersion?: string;
};

// NOTE: exported for testing purposes only
export type EventProperties = BaseProperties &
  DateProperties &
  PetTypesProperties &
  SearchAlgorithmProperties;

// NOTE: exported for testing purposes only
export const getRepeatFrequency = (searchFilters: SearchFilters): string => {
  const {
    bookingType,
    service: { type: serviceType },
  } = searchFilters;

  const isRwbBooking = bookingType === BOOKING_TYPE_RWB && RWB_SERVICE_NAMES.includes(serviceType);

  return isRwbBooking ? 'repeat weekly' : 'one off';
};

// NOTE: exported for testing purposes only
export const getDatesForOvernightService = (chronology: Chronology): DateProperties => {
  const { startDate, endDate } = chronology;

  const overnightLengthOfStay = dayjs(endDate).diff(startDate, 'day');
  const leadTime = dayjs(startDate).diff(dayjs().format('YYYY-MM-DD'), 'day');

  return {
    check_in: startDate as string,
    check_out: endDate as string,
    overnight_length_of_stay: overnightLengthOfStay,
    lead_time: leadTime,
  };
};

// NOTE: exported for testing purposes only
export const getDatesForRwbService = (chronology: Chronology): DateProperties => {
  const { rwbStartDate, weekDays } = chronology;

  const rwbServiceNumberDays = Object.entries(weekDays).filter(([, value]) => value).length;
  const leadTime = dayjs(rwbStartDate).diff(dayjs().format('YYYY-MM-DD'), 'day');

  return {
    check_in: rwbStartDate,
    rwb_service_number_days: rwbServiceNumberDays,
    lead_time: leadTime,
    daytime_service_monday: weekDays.monday,
    daytime_service_tuesday: weekDays.tuesday,
    daytime_service_wednesday: weekDays.wednesday,
    daytime_service_thursday: weekDays.thursday,
    daytime_service_friday: weekDays.friday,
    daytime_service_saturday: weekDays.saturday,
    daytime_service_sunday: weekDays.sunday,
  };
};

// NOTE: exported for testing purposes only
export const getDatesForDaytimeService = (chronology: Chronology): DateProperties => {
  const { scheduledDates } = chronology;

  const checkIn = scheduledDates?.sort()[0] || '';
  const singleServiceNumberDays = scheduledDates?.length;
  const leadTime = dayjs(checkIn).diff(dayjs().format('YYYY-MM-DD'), 'day') || 0;

  return {
    check_in: checkIn,
    single_service_number_days: singleServiceNumberDays,
    lead_time: leadTime,
  };
};

// NOTE: exported for testing purposes only
export const getDatesProperties = (searchFilters: SearchFilters): DateProperties => {
  const {
    service: { type: serviceType },
    bookingType,
    chronology,
  } = searchFilters;

  const isOvernightService = OVERNIGHT_SERVICE_NAMES.includes(serviceType);
  const isRwbBooking = bookingType === BOOKING_TYPE_RWB && RWB_SERVICE_NAMES.includes(serviceType);

  if (isOvernightService) {
    return getDatesForOvernightService(chronology);
  }

  if (isRwbBooking) {
    return getDatesForRwbService(chronology);
  }

  return getDatesForDaytimeService(chronology);
};

// NOTE: exported for testing purposes only
export const getPetTypesProperties = (searchFilters: SearchFilters): PetTypesProperties => {
  const { petTypes } = searchFilters;

  return {
    small_dog_number: Number(petTypes.smallDog),
    medium_dog_number: Number(petTypes.mediumDog),
    large_dog_number: Number(petTypes.largeDog),
    giant_dog_number: Number(petTypes.giantDog),
    puppy_number: Number(petTypes.puppy),
    cat_number: Number(petTypes.cat),
    critter_number: Number(petTypes.creature),
    bird_number: Number(petTypes.bird),
  };
};

export const getEventProperties = (
  searchFilters: SearchFilters,
  searchResults?: SearchResults
): EventProperties => {
  const repeatFrequency = getRepeatFrequency(searchFilters);
  const datesProperties = getDatesProperties(searchFilters);
  const petTypesProperties = getPetTypesProperties(searchFilters);

  const {
    location: {
      address: { suburb },
    },
    service: { type: serviceType },
  } = searchFilters;

  return {
    suburb,
    service_type: SERVICE_TYPE_MAPPING[serviceType] ?? DEFAULT_SERVICE_NAME,
    repeat_frequency: repeatFrequency,
    search_algorithmVersion: searchResults?.metaInfo.searchVersion,
    ...datesProperties,
    ...petTypesProperties,
  };
};

// exported for testing purposes only
export const getEventAdvancedProperties = (
  searchFilters: SearchFilters
): FiltersDialogEventProperties => ({
  about_sitter: searchFilters.advancedFilters?.aboutSitter,
  sort: searchFilters.sort,
  reviews_min: searchFilters.advancedFilters?.reviewsMin,
  pets_kids_sitter_home: searchFilters.advancedFilters?.sittersPetsKids,
  sitter_home: searchFilters.advancedFilters?.sittersHome,
  lower_price: searchFilters.advancedFilters?.price[0],
  upper_price: searchFilters.advancedFilters?.price[1],
});

export const getEventAllProperties = (searchFilters: SearchFilters): EventProperties => {
  const commonProperties = getEventProperties(searchFilters);
  const advancedFiltersProperties = getEventAdvancedProperties(searchFilters);

  return {
    ...commonProperties,
    ...advancedFiltersProperties,
  };
};
